import { ShieldCheck, ShieldQuestion, Unlock, UserCheck } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from '@unistocks/components/form/form';
import { Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { toast } from '@unistocks/components/toast';
import { UpdateIdentitySettingsDto } from '@unistocks/proxy/axios-client';
import {
  identitySettingsGetQueryKey,
  useIdentitySettingsGetQuery,
  useIdentitySettingsUpdateMutation
} from '@unistocks/proxy/axios-client/IdentitySettingsQuery';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { t } from '@unistocks/shared/localization/index.ts';
import { Separator } from 'tamagui';
import { z } from 'zod';

const schema = z.object({
  signIn: z.object({
    requireConfirmedEmail: z.boolean(),
    enablePhoneNumberConfirmation: z.boolean(),
    requireConfirmedPhoneNumber: z.boolean()
  }),
  user: z.object({
    isUserNameUpdateEnabled: z.boolean(),
    isEmailUpdateEnabled: z.boolean()
  }),
  lockout: z.object({
    allowedForNewUsers: z.boolean(),
    duration: z.number().min(0),
    maxFailedAccessAttempts: z.number().min(0)
  }),
  password: z.object({
    requireDigit: z.boolean(),
    requireLowercase: z.boolean(),
    requireUppercase: z.boolean(),
    requireNonAlphanumeric: z.boolean(),
    requiredLength: z.number().min(1),
    requiredUniqueChars: z.number().min(0)
  })
});

export const IdentitySettings: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: identitySettings } = useIdentitySettingsGetQuery();

  const { mutateAsync } = useIdentitySettingsUpdateMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
      // Identity settings also have their own query separate from app config values.
      queryClient.invalidateQueries({ queryKey: identitySettingsGetQueryKey() });
      toast.success(t('AbpUi::SavedSuccessfully'));
    }
  });

  const initialValues: UpdateIdentitySettingsDto = {
    signIn: {
      requireConfirmedEmail: identitySettings?.signIn?.requireConfirmedEmail ?? false,
      enablePhoneNumberConfirmation:
        identitySettings?.signIn?.enablePhoneNumberConfirmation ?? false,
      requireConfirmedPhoneNumber: identitySettings?.signIn?.requireConfirmedPhoneNumber ?? false
    },
    user: {
      isUserNameUpdateEnabled: identitySettings?.user?.isUserNameUpdateEnabled ?? false,
      isEmailUpdateEnabled: identitySettings?.user?.isEmailUpdateEnabled ?? false
    },
    lockout: {
      allowedForNewUsers: identitySettings?.lockout?.allowedForNewUsers ?? false,
      duration: identitySettings?.lockout?.duration ?? 0,
      maxFailedAccessAttempts: identitySettings?.lockout?.maxFailedAccessAttempts ?? 0
    },
    password: {
      requireDigit: identitySettings?.password?.requireDigit ?? false,
      requireLowercase: identitySettings?.password?.requireLowercase ?? false,
      requireUppercase: identitySettings?.password?.requireUppercase ?? false,
      requireNonAlphanumeric: identitySettings?.password?.requireNonAlphanumeric ?? false,
      requiredLength: identitySettings?.password?.requiredLength ?? 6,
      requiredUniqueChars: identitySettings?.password?.requiredUniqueChars ?? 0
    }
  };

  return (
    <QueryState queryKeys={[identitySettingsGetQueryKey()]}>
      <Page.Content>
        <Form
          schema={schema}
          initialValues={initialValues}
          onSubmit={values => mutateAsync(values as UpdateIdentitySettingsDto)}
          gap={'$4'}
        >
          <Form.Section label={t('AbpIdentity::SignIn')} Icon={ShieldCheck}>
            <Form.Checkbox
              name="signIn.requireConfirmedEmail"
              label={t('AbpIdentity::DisplayName:Abp.Identity.SignIn.RequireConfirmedEmail')}
            />
            <Form.Checkbox
              name="signIn.enablePhoneNumberConfirmation"
              label={t(
                'AbpIdentity::DisplayName:Abp.Identity.SignIn.EnablePhoneNumberConfirmation'
              )}
            />
            <Form.Checkbox
              name="signIn.requireConfirmedPhoneNumber"
              label={t('AbpIdentity::DisplayName:Abp.Identity.SignIn.RequireConfirmedPhoneNumber')}
            />
          </Form.Section>

          <Separator />

          <Form.Section label={t('AbpIdentity::Password')} Icon={ShieldQuestion}>
            <Row flexWrap="wrap" mb={'$3'}>
              <Form.Checkbox
                name="password.requireDigit"
                label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequireDigit')}
              />
              <Form.Checkbox
                name="password.requireLowercase"
                label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequireLowercase')}
              />
              <Form.Checkbox
                name="password.requireUppercase"
                label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequireUppercase')}
              />
              <Form.Checkbox
                name="password.requireNonAlphanumeric"
                label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequireNonAlphanumeric')}
              />
            </Row>

            <Form.Input
              name="password.requiredLength"
              keyboardType="numeric"
              label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequiredLength')}
            />
            <Form.Input
              name="password.requiredUniqueChars"
              keyboardType="numeric"
              label={t('AbpIdentity::DisplayName:Abp.Identity.Password.RequiredUniqueChars')}
            />
          </Form.Section>

          <Separator />

          <Form.Section label={t('AbpIdentity::Lockout')} Icon={Unlock}>
            <Form.Checkbox
              name="lockout.allowedForNewUsers"
              label={t('AbpIdentity::DisplayName:Abp.Identity.Lockout.AllowedForNewUsers')}
            />
            <Form.Input
              name="lockout.duration"
              keyboardType="numeric"
              label={t('AbpIdentity::DisplayName:Abp.Identity.Lockout.LockoutDuration')}
              placeholder="0"
            />
            <Form.Input
              name="lockout.maxFailedAccessAttempts"
              keyboardType="numeric"
              label={t('AbpIdentity::DisplayName:Abp.Identity.Lockout.MaxFailedAccessAttempts')}
              placeholder="0"
            />
          </Form.Section>

          <Separator />

          <Form.Section label={t('AbpIdentity::UserInformations')} Icon={UserCheck}>
            <Form.Checkbox
              name="user.isUserNameUpdateEnabled"
              label={t('AbpIdentity::DisplayName:Abp.Identity.User.IsUserNameUpdateEnabled')}
            />
            <Form.Checkbox
              name="user.isEmailUpdateEnabled"
              label={t('AbpIdentity::DisplayName:Abp.Identity.User.IsEmailUpdateEnabled')}
            />
          </Form.Section>
        </Form>
      </Page.Content>
    </QueryState>
  );
};

IdentitySettings.displayName = 'IdentitySettings';
